import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  LocationOn,
  Camera,
  Coffee,
  AccessTime,
} from '@mui/icons-material';
import { AttendanceRecord } from '../../services/attendanceService';
import breakService, { Break } from '../../services/breakService';
import ProfileAvatar from '../common/ProfileAvatar';
import ImageDialog from '../common/ImageDialog';

interface AttendanceDetailModalProps {
  open: boolean;
  onClose: () => void;
  attendance: AttendanceRecord | null;
}

const AttendanceDetailModal: React.FC<AttendanceDetailModalProps> = ({ open, onClose, attendance }) => {
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = React.useState<string | null>(null);
  const [selectedImageAlt, setSelectedImageAlt] = React.useState<string>('');
  const [breaks, setBreaks] = useState<Break[]>([]);
  const [loadingBreaks, setLoadingBreaks] = useState(false);

  useEffect(() => {
    if (open && attendance?.id) {
      loadBreaks();
    } else {
      setBreaks([]);
    }
  }, [open, attendance?.id]);

  const loadBreaks = async () => {
    if (!attendance?.id) return;
    setLoadingBreaks(true);
    try {
      const breaksData = await breakService.getBreaksByAttendance(attendance.id);
      setBreaks(breaksData);
    } catch (error) {
      console.error('Error loading breaks:', error);
      setBreaks([]);
    } finally {
      setLoadingBreaks(false);
    }
  };

  if (!attendance) return null;

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getStatusColor = (status: string, isLate?: boolean) => {
    // Show warning (orange) if late, regardless of status
    if (isLate) return 'warning';
    switch (status) {
      case 'in':
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late': return 'warning';
      case 'out': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string, isLate?: boolean, lateMinutes?: number) => {
    // Show "Late" if is_late is true, regardless of status
    if (isLate) {
      return lateMinutes ? `${lateMinutes}m Late` : 'Late';
    }
    switch (status) {
      case 'in': return 'Present';
      case 'out': return 'Checked Out';
      case 'absent': return 'Absent';
      case 'late': return 'Late';
      case 'no_punch': return 'No Punch';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Attendance Details
          </Typography>
          <Chip
            label={getStatusLabel(attendance.status, attendance.is_late, attendance.late_minutes)}
            color={getStatusColor(attendance.status, attendance.is_late) as any}
            icon={attendance.is_late ? <Schedule /> : (attendance.status === 'in' ? <CheckCircle /> : <Schedule />)}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Employee Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <ProfileAvatar
                    imageUrl={attendance.user?.profile_image_url}
                    firstName={attendance.user?.first_name}
                    lastName={attendance.user?.last_name}
                    sx={{ width: 64, height: 64, bgcolor: '#081122' }}
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#081122', fontWeight: 600 }}>
                      Employee Information
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {attendance.user?.first_name} {attendance.user?.last_name}
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Name</Typography>
                    <Typography variant="body1">
                      {attendance.user?.first_name} {attendance.user?.last_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Employee ID</Typography>
                    <Typography variant="body1">{attendance.user?.employee_id}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Store</Typography>
                    <Typography variant="body1">{attendance.user?.store?.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Cluster</Typography>
                    <Typography variant="body1">{attendance.user?.cluster?.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Shift</Typography>
                    <Typography variant="body1">
                      {attendance.shift?.name || attendance.user?.shift?.name || 'N/A'}
                      {attendance.shift?.start_time && attendance.shift?.end_time && (
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                          {attendance.shift.start_time} - {attendance.shift.end_time}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Date Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                  Date Information
                </Typography>
                <Typography variant="body1">{formatDate(attendance.date)}</Typography>
                <Box display="flex" gap={2} mt={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666' }}>Clock In</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatTime(attendance.clock_in_time)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666' }}>Clock Out</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatTime(attendance.clock_out_time)}
                    </Typography>
                  </Box>
                  {attendance.total_hours && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#666' }}>Total Hours</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {typeof attendance.total_hours === 'number' 
                          ? attendance.total_hours.toFixed(1) 
                          : parseFloat(attendance.total_hours as string || '0').toFixed(1)}h
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Location Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                  Location Information
                </Typography>
                <Grid container spacing={2}>
                  {attendance.clock_in_latitude && attendance.clock_in_longitude && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#4caf50' }}>
                          <LocationOn sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                          Punch In Location
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Latitude: {attendance.clock_in_latitude}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Longitude: {attendance.clock_in_longitude}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {attendance.clock_out_latitude && attendance.clock_out_longitude && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#f44336' }}>
                          <LocationOn sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                          Punch Out Location
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Latitude: {attendance.clock_out_latitude}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Longitude: {attendance.clock_out_longitude}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Photos */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#081122', fontWeight: 600 }}>
                  Photos
                </Typography>
                <Grid container spacing={2}>
                  {attendance.clock_in_photo_url ? (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
                        <Camera sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                        Punch In Photo
                      </Typography>
                      <img
                        src={attendance.clock_in_photo_url}
                        alt="Punch In"
                        style={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 8,
                          cursor: 'pointer',
                          border: '1px solid #e0e0e0'
                        }}
                        onClick={() => {
                          setSelectedImageUrl(attendance.clock_in_photo_url || null);
                          setSelectedImageAlt('Punch In Photo');
                          setImageDialogOpen(true);
                        }}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        No punch in photo available
                      </Typography>
                    </Grid>
                  )}
                  
                  {attendance.clock_out_photo_url ? (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
                        <Camera sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                        Punch Out Photo
                      </Typography>
                      <img
                        src={attendance.clock_out_photo_url}
                        alt="Punch Out"
                        style={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 8,
                          cursor: 'pointer',
                          border: '1px solid #e0e0e0'
                        }}
                        onClick={() => {
                          setSelectedImageUrl(attendance.clock_out_photo_url || null);
                          setSelectedImageAlt('Punch Out Photo');
                          setImageDialogOpen(true);
                        }}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        No punch out photo available
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Late Information */}
          {attendance.is_late && attendance.late_minutes && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: '#fff3cd' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: '#856404', fontWeight: 600 }}>
                    ⚠️ Late Arrival
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: '#856404' }}>
                    Late by {attendance.late_minutes} minutes
                  </Typography>
                  {attendance.shift?.start_time && (
                    <Typography variant="caption" sx={{ color: '#856404', display: 'block', mt: 1 }}>
                      Shift start time: {attendance.shift.start_time} | Grace period: 15 minutes
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Break Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Coffee sx={{ color: '#081122' }} />
                  <Typography variant="subtitle2" sx={{ color: '#081122', fontWeight: 600 }}>
                    Breaks
                  </Typography>
                </Box>
                {loadingBreaks ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : breaks.length > 0 ? (
                  <Box>
                    {breaks.map((breakItem, index) => (
                      <Box key={breakItem.id}>
                        {index > 0 && <Divider sx={{ my: 2 }} />}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ color: '#666' }}>Start Time</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatTime(breakItem.start_time)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ color: '#666' }}>End Time</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {breakItem.end_time ? formatTime(breakItem.end_time) : 'Ongoing'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ color: '#666' }}>Duration</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {breakItem.duration_minutes 
                                ? `${breakItem.duration_minutes} minutes`
                                : breakItem.end_time 
                                  ? 'Calculating...'
                                  : 'In progress'}
                            </Typography>
                          </Grid>
                          {breakItem.extra_minutes > 0 && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ color: '#f44336' }}>Extra Time</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#f44336' }}>
                                +{breakItem.extra_minutes} minutes
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Chip
                              label={breakItem.status.charAt(0).toUpperCase() + breakItem.status.slice(1)}
                              size="small"
                              color={
                                breakItem.status === 'active' ? 'warning' :
                                breakItem.status === 'completed' ? 'success' :
                                'error'
                              }
                              icon={<AccessTime />}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                    No breaks taken on this day
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ background: '#081122', '&:hover': { background: '#1a2a3a' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Image Dialog */}
    <ImageDialog
      open={imageDialogOpen}
      onClose={() => {
        setImageDialogOpen(false);
        setSelectedImageUrl(null);
        setSelectedImageAlt('');
      }}
      imageUrl={selectedImageUrl}
      alt={selectedImageAlt}
    />
    </>
  );
};

export default AttendanceDetailModal;

